/* eslint-disable spellcheck/spell-checker */
import { computed } from '@ember/object';
import { equal } from '@ember/object/computed';
import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo } from 'ember-data/relationships';
import { A } from '@ember/array';
import { modelApiAction } from 'cio/utils/model-api-actions';
import {
  USER_ROLE_ADMIN,
  USER_ROLE_AUTHOR,
  USER_ROLE_VIEWER,
  USER_ROLE_VIEWER_REDACT,
  USER_ROLE_AUTHOR_REDACT,
} from 'cio/utils/user-roles';

import { IdentityProvider } from 'cio/models/sso-configuration';

import { inject as service } from '@ember/service';
import ApiService, { RequestOptions } from 'cio/services/api';
import EnvironmentService from 'cio/services/environment';
import MasqService, { ADMIN_ACCOUNT_ID } from 'cio/services/masq';
import AuthService from 'cio/services/auth';
import { stringToColor } from 'cio/utils/colors';
import EnvironmentModel from 'cio/models/environment';
import { isNone } from '@ember/utils';
import { Permission } from '@customerio/hydra-permissions';
import { Role, permissionsForRole } from '@customerio/hydra-permissions/roles';
import AccountService from 'cio/services/account';

export enum MasqPermissions {
  SuperUser = 'superuser',
  ReadOnlyUser = 'readonly',
  ReadWriteUser = 'readwrite',
  MasqLevelNotSet = 'none',
  NoMasqAccess = '',
}

interface TwoFactorAuthCodesResponse {
  codes: string[];
}

interface SetPasswordResponse {
  access_token: string;
  session_expires_at: number;
}

interface SetEmailResponse {
  access_token: string;
}

export type CampaignSubscription = {
  environment_id: number;
  campaign_id: number;
};

export type UserSettings = {
  segment_ids: string[];
  delivery: {
    channels: string[];
  };
  performance: {
    channels: string[];
  };
  campaigns_page: {
    sort: string;
    columns: string[];
    is_columns_updated: boolean;
    preset_range: string;
  };
};

export type UserFlags = {
  show_denominator_change_release_modal: boolean;
  show_composer_minimap: boolean;
  show_liquid_fallback_modal: boolean;
  save_auto_refresh: boolean;
  aggressive_positivity_available: boolean;
  aggressive_positivity_mode_on: boolean;
  show_segment_json_syntax_support_banner: boolean;
  parcel_editor_on: boolean;
  show_mobile_sdk_required_banner: boolean;
  show_parcel_onboarding_tour: boolean;
  show_parcel_amp: boolean;
  show_webhook_signing_secret_banner: boolean;
  show_live_language_translation_edit_warning: boolean;
  show_campaign_object_trigger_ui_banner: boolean;
  show_objects_liquid_via_ai: boolean;
  show_ai_search_banner: boolean;
  show_chat_sparkles: boolean;
};

export type GoogleDriveCredential = {
  account: string;
};

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export default class UserModel extends Model {
  @service api!: ApiService;
  @service account!: AccountService;
  @service auth!: AuthService;
  @service masq!: MasqService;
  // Calling this environmentService to avoid confusion as to whether
  // "environment" would be the service or an association to the environment
  // model
  @service('environment') environmentService!: EnvironmentService;
  @service store!: DS.Store;

  @attr('string') email!: string;
  @attr('string') firstName!: string;
  @attr('string') lastName!: string;
  @attr('string') role!: Role;
  @attr('boolean') canMasquerade!: boolean;
  @attr('boolean') canMasqueradeWrite!: boolean;
  @attr('number') createdAt!: number;
  @attr('number') currentSignInAt!: number;
  @attr('number') lastSignInAt!: number;
  @attr('boolean', { allowNull: true }) twoFactorAuthenticationEnabled!: boolean | null;
  @attr() userFlags!: UserFlags; // JSON object
  @attr() googleDriveCredential!: GoogleDriveCredential; // JSON object
  @attr('boolean', { allowNull: true }) twoFactorAuthenticationEmailEnabled!: boolean | null;

  @attr('number') importMaxSizeBytes!: number;
  @attr('string') externalIdentityProvider!: IdentityProvider | '';
  @attr('string') externalIdentityEmail!: string;

  // "accessibleEnvironments" is a getter/setter based on
  // "accessibleEnvironmentIds" instead of a @hasMany relationship
  // to avoid problems when one of the environments is or gets deleted.
  //
  // Ember Data 4.12 treats relationships fairly strictly, so deleting an
  // environment which is present in user's "accessibleEnvironments" throws an
  // error.
  @attr('array') accessibleEnvironmentIds!: string[];

  @computed('accessibleEnvironmentIds.[]')
  get accessibleEnvironments() {
    return (
      (this.accessibleEnvironmentIds ?? [])
        .map((id) => this.store.peekRecord('environment', id))

        // Filter out possible deleted environments:
        //
        // Also, in opsbugs#2364 admin user had an environment that is not present loaded in environmentService
        // causing model's properties to be undefined breaking this attribute.
        // This filter prevents them from being returned here.
        // (Adds an exception for accounts that don't have Journeys enabled, since
        // they use Hydra Workspaces and these don't have createdAt)
        .filter((env) => {
          if (env == null) {
            return false;
          }
          if (!this.account.isJourneysAvailable) {
            return true;
          }
          return !isNone(env.createdAt);
        }) as EnvironmentModel[]
    );
  }
  set accessibleEnvironments(envs: EnvironmentModel[]) {
    this.set(
      'accessibleEnvironmentIds',
      envs.map((item) => item.id),
    );
  }

  @belongsTo('environment', { async: true, inverse: null }) lastEnvironment!: EnvironmentModel;
  @attr('string') masqPermissions!: MasqPermissions;

  @computed('accessibleEnvironmentIds.[]')
  get defaultEnvironment() {
    return this.accessibleEnvironments.sort((a, b) => (a?.createdAt ?? 0) - (b?.createdAt ?? 0))[0];
  }

  canAccessEnvironment(id: EnvironmentModel['id']) {
    /** TODO: Quick fix for being able to access workspace-settings on
     * a CDP-only account, with no Journeys environments. This works,
     * but skips comparing the value with the actual existing workspaces,
     * which should be done in the near future.  */
    if (!this.account.isJourneysAvailable) {
      return this.accessibleEnvironmentIds.includes(id);
    }
    return this.accessibleEnvironments.some((environment) => environment.id === id);
  }

  @equal('role', USER_ROLE_ADMIN) isAdmin!: boolean;
  @computed('role')
  get isReadOnly() {
    return [USER_ROLE_VIEWER, USER_ROLE_VIEWER_REDACT].includes(this.role);
  }
  @computed('role')
  get isAuthor() {
    return [USER_ROLE_AUTHOR, USER_ROLE_AUTHOR_REDACT].includes(this.role);
  }
  @computed('role')
  get isViewer() {
    return [USER_ROLE_VIEWER, USER_ROLE_VIEWER_REDACT].includes(this.role);
  }
  @computed('role')
  get isRedacted() {
    return [USER_ROLE_VIEWER_REDACT, USER_ROLE_AUTHOR_REDACT].includes(this.role);
  }

  @equal('masqPermissions', MasqPermissions.SuperUser) isSuperUser!: boolean;
  @equal('masqPermissions', MasqPermissions.ReadOnlyUser) canReadOnlyMasq!: boolean;
  @equal('masqPermissions', MasqPermissions.ReadWriteUser) canReadWriteMasq!: boolean;
  @computed('masqPermissions')
  get noMasqAccess() {
    return this.masqPermissions == null || this.masqPermissions === MasqPermissions.NoMasqAccess;
  }
  @equal('masqPermissions', MasqPermissions.MasqLevelNotSet) masqLevelNotSet!: boolean;

  @computed('masqPermissions')
  get hasReadMasqAccess() {
    return this.canReadOnlyMasq || this.canReadWriteMasq;
  }

  @computed('email')
  get emailDomain(): string {
    return this.email.split('@')[1] || '';
  }

  @computed('firstName', 'lastName')
  get fullName() {
    let { firstName, lastName } = this;
    return A([firstName || null, lastName || null])
      .compact()
      .join(' ');
  }

  @computed('fullName')
  get uniqueColor() {
    return stringToColor(this.fullName);
  }

  @computed('currentSignInAt', 'lastSignInAt')
  get lastLoginAt() {
    // lastSignInAt actually means "previous sign in time". It can be 0 if user signed in only once.
    return Math.max(this.lastSignInAt, this.currentSignInAt);
  }

  // Eventually this will be an attribute returned by the back end rather than
  // a large computed object
  get permissions(): Record<Permission, boolean> {
    let { role } = this;

    /* eslint-disable @typescript-eslint/naming-convention */
    return permissionsForRole(role);
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  _setEmail = modelApiAction({ path: 'set_email', type: 'POST' });
  setEmail(email: string, currentPassword: string) {
    return this._setEmail({ user: { email, currentPassword } }).then((res: SetEmailResponse) => ({
      success: res.access_token,
    }));
  }

  resetPassword = modelApiAction({ path: 'reset_password', type: 'POST' });
  _setPassword = modelApiAction({ path: 'set_password', type: 'POST' });
  setPassword(password: string, currentPassword: string) {
    return this._setPassword({
      user: {
        current_password: currentPassword,
        password,
        password_confirmation: password,
      },
    }).then((res: SetPasswordResponse) => ({
      accessToken: res.access_token,
      sessionExpiresAt: res.session_expires_at,
    }));
  }

  getTwoFactorAuthenticationSecret = modelApiAction({
    path: '2fa_secret',
    type: 'GET',
  });

  setupTwoFactorAuthentication = modelApiAction({
    path: '2fa_setup',
    type: 'POST',
  });

  confirmTwoFactorAuthenticationBackupCodes = modelApiAction({
    path: '2fa_confirm_backup_codes',
    type: 'POST',
  });

  _getTwoFactorAuthenticationBackupCodes = modelApiAction({
    path: '2fa_backup_codes',
    type: 'GET',
  });
  getTwoFactorAuthenticationBackupCodes(): Promise<string[]> {
    return this._getTwoFactorAuthenticationBackupCodes({}).then(({ codes }: TwoFactorAuthCodesResponse) => codes);
  }

  _generateNewTwoFactorAuthenticationBackupCodes = modelApiAction({
    path: '2fa_generate_backup_codes',
    type: 'POST',
  });
  generateNewTwoFactorAuthenticationBackupCodes(): Promise<string[]> {
    return this._generateNewTwoFactorAuthenticationBackupCodes({}).then(
      ({ codes }: TwoFactorAuthCodesResponse) => codes,
    );
  }

  turnOffTwoFactorAuthentication = modelApiAction({
    path: '2fa_setup_destroy',
    type: 'POST',
  });

  getSubscriptions = modelApiAction({
    path: 'subscriptions',
    type: 'GET',
    after(response): CampaignSubscription[] {
      if (!response.weekly) {
        return [];
      }

      return response.weekly;
    },
  });

  followCampaign = modelApiAction({
    path: 'subscriptions/weekly',
    type: 'PUT',
    before(options) {
      return {
        campaign_id: parseInt(options.campaignId),
        environment_id: parseInt(options.environmentId),
      };
    },
  });

  unfollowCampaign = modelApiAction({
    path: 'subscriptions/weekly',
    type: 'DELETE',
    before(options) {
      return {
        campaign_id: parseInt(options.campaignId),
        environment_id: parseInt(options.environmentId),
      };
    },
  });

  resendConfirmationEmail = modelApiAction({ path: 'resend_confirmation_email', type: 'POST' });

  _setExternalIdentity = modelApiAction<unknown, void>({
    path: 'external_identity',
    type: 'POST',
  });
  setExternalIdentity({
    code,
    redirectUri,
    currentPassword,
  }: {
    code: string;
    redirectUri: string;
    currentPassword: string;
  }): Promise<void> {
    return this._setExternalIdentity({ code, redirect_uri: redirectUri, current_password: currentPassword });
  }

  destroyExternalIdentity = modelApiAction({
    path: 'external_identity',
    type: 'DELETE',
  });

  updateUserFlag(key: keyof UserFlags, value: boolean) {
    this.set('userFlags', {
      ...this.userFlags,
      [key]: value,
    });
    return this.updateUserFlags({});
  }
  updateUserFlags = modelApiAction({
    path: 'toggle_user_flags',
    type: 'POST',
    before(this: UserModel) {
      return this.userFlags;
    },
    after(this: UserModel, response) {
      let filteredResponse = {
        user: {
          id: response?.user?.id ?? this.id,
          user_flags: response?.user?.user_flags ?? {},
        },
      };
      this.store.pushPayload(filteredResponse);
      return filteredResponse;
    },
  });

  createGoogleDriveCredential = modelApiAction({
    path: 'google_drive_credential',
    type: 'POST',
    after(this: UserModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  deleteGoogleDriveCredential = modelApiAction({
    path: 'google_drive_credential',
    type: 'DELETE',
    after(this: UserModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  getGoogleDriveAccessToken = modelApiAction({
    path: 'google_drive_credential/access_token',
    type: 'GET',
    after(response) {
      return response.access_token;
    },
  });

  async getUserSettings(): Promise<Partial<UserSettings>> {
    let environmentId = this.environmentService.current.id;

    if (environmentId == null) {
      return {};
    }

    let apiOptions: RequestOptions = { pathScope: 'account' };
    if (this.masq.isOn && this.id === this.auth.user?.id) {
      // consider that masqing user always belongs to admin CIO account ID=1, use it when building URL
      apiOptions = { ...apiOptions, replaceAccountID: String(ADMIN_ACCOUNT_ID) };
    }
    let result = await this.api.request(`users/${this.id}/settings/${environmentId}`, apiOptions);

    if (result.segment_ids) {
      // Ember Data uses strings for model IDs, but the API here gives us
      // integers, so we need to convert them
      result.segment_ids = result.segment_ids.map(String);
    }

    return result;
  }

  async updateUserSettings(settings: DeepPartial<UserSettings>) {
    let environmentId = this.environmentService.current.id;

    if (!environmentId) {
      return {};
    }

    let apiData: Record<string, any> = {
      ...settings,
    };
    if (apiData.segment_ids) {
      // Map segment ID strings back to the integers that the API expects
      apiData.segment_ids = apiData.segment_ids.map(Number);
    }

    let apiOptions: RequestOptions = { pathScope: 'account', data: apiData };
    if (this.masq.isOn && this.id === this.auth.user?.id) {
      // consider that masqing user always belongs to admin CIO account ID=1, use it when building URL
      apiOptions = { ...apiOptions, replaceAccountID: String(ADMIN_ACCOUNT_ID) };
    }
    return this.api.put(`users/${this.id}/settings/${environmentId}`, apiOptions);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    user: UserModel;
  }
}
